/**
 * Parse a horse's saddlecloth number and name from prediction data.
 *
 * The backend delivers a single `formattedName` string of the form
 * `"7. Sorastorm"`. Occasionally it arrives with no `"<number>. "` prefix (just
 * the name, e.g. `"Cavallo Red"`) or otherwise malformed. A naive
 * `formattedName.split(". ")` then drops the whole name into the *number* slot
 * and leaves the name blank — the "names and numbers in the wrong place" bug.
 *
 * This parses defensively:
 *  - prefer explicit `horse.number` / `horse.name` when the backend provides them;
 *  - otherwise split on the first `". "` and accept the prefix as the number
 *    ONLY when it is actually numeric;
 *  - never put a non-numeric token in the number slot — an unparseable value
 *    yields the whole string as the name and a blank number.
 *
 * @param {{ formattedName?: string, number?: string|number, name?: string }} horse
 * @returns {{ number: string, name: string }} Number is "" when unknown.
 */
export function parseHorseFormattedName(horse) {
  const formatted =
    typeof horse?.formattedName === "string" ? horse.formattedName.trim() : "";

  // Explicit fields win when present.
  let number =
    horse?.number != null && String(horse.number).trim() !== ""
      ? String(horse.number).trim()
      : "";
  let name =
    typeof horse?.name === "string" && horse.name.trim() !== ""
      ? horse.name.trim()
      : "";

  if (!number || !name) {
    const sep = formatted.indexOf(". ");
    const prefix = sep === -1 ? "" : formatted.slice(0, sep).trim();
    const rest = sep === -1 ? formatted : formatted.slice(sep + 2).trim();

    if (/^\d+$/.test(prefix)) {
      if (!number) number = prefix;
      if (!name) name = rest;
    } else if (!name) {
      // No valid "<number>. " prefix — the whole string is the name; the
      // number stays blank rather than absorbing the name.
      name = formatted;
    }
  }

  return { number, name };
}
